import { PayoutStatus } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { PayinRuntimeStatus } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import type { PaymentsActivityTranslations } from "@/shared/lang/interfaces/paymentsActivity";
import { formatRail } from "@/shared/utils/railDisplay";

import type { ActivityDirection, ActivityStatusBucket } from "../types/paymentsActivity.types";

/**
 * Polling cadence (design brief §6): there is NO websocket/SSE for payments — "live status" is polling. List
 * refreshes only while at least one visible row is non-terminal; the drawer polls its own entity.
 */
export const LIST_POLL_INTERVAL_MS = 15_000;
export const DETAIL_POLL_INTERVAL_MS = 6_000;

/** Backend pagination: offset-based, default limit 10, MAX 200. */
export const ACTIVITY_PAGE_SIZE = 25;

/**
 * Non-terminal statuses per direction (keep polling). Mirrors the backend transition tables: payin
 * CREATED→PENDING→CONFIRMED, payout CREATED→PROCESSING→PAID; everything else is terminal.
 */
export const PAYIN_NON_TERMINAL: ReadonlySet<string> = new Set([
  PayinRuntimeStatus.CREATED,
  PayinRuntimeStatus.PENDING,
]);
export const PAYOUT_NON_TERMINAL: ReadonlySet<string> = new Set([
  PayoutStatus.CREATED,
  PayoutStatus.PROCESSING,
]);

/** Raw status → display bucket, per direction (the two vocabularies are DIFFERENT — never share one map). */
export const PAYIN_STATUS_BUCKET: Record<PayinRuntimeStatus, ActivityStatusBucket> = {
  [PayinRuntimeStatus.CREATED]: "pending",
  [PayinRuntimeStatus.PENDING]: "processing",
  [PayinRuntimeStatus.CONFIRMED]: "settled",
  [PayinRuntimeStatus.REJECTED]: "failed",
  [PayinRuntimeStatus.FAILED]: "failed",
  [PayinRuntimeStatus.REVERSED]: "failed",
};
export const PAYOUT_STATUS_BUCKET: Record<PayoutStatus, ActivityStatusBucket> = {
  [PayoutStatus.CREATED]: "pending",
  [PayoutStatus.PROCESSING]: "processing",
  [PayoutStatus.PAID]: "settled",
  [PayoutStatus.FAILED]: "failed",
  // CANCELLED is a deliberate user action (funds released before dispatch) — never render it as an error.
  [PayoutStatus.CANCELLED]: "cancelled",
  [PayoutStatus.REVERSED]: "failed",
};

/**
 * Bucket → `ColocaBadge` variant, the same status vocabulary `cards` and `wallets` use.
 *
 * `processing` is deliberately the outline variant rather than a green one: it is in flight, not a verdict,
 * and any green would read as settled at a glance. What signals "live" is the pulsing dot on non-terminal
 * rows, not the color. `cancelled` stays neutral — it is a deliberate user action, never an error.
 */
export const BUCKET_BADGE_VARIANT: Record<
  ActivityStatusBucket,
  "success" | "warning" | "destructive" | "secondary" | "default"
> = {
  settled: "success",
  processing: "default",
  pending: "warning",
  failed: "destructive",
  cancelled: "secondary",
};

/**
 * Raw status → display bucket for the ACTIVE direction. The two vocabularies overlap in name but not in
 * meaning, so the direction decides which map answers. Returns null for a status the runtime added after this
 * build — callers must degrade rather than guess.
 */
export function bucketForStatus(direction: ActivityDirection, status: string): ActivityStatusBucket | null {
  const map: Record<string, ActivityStatusBucket> =
    direction === "payins" ? PAYIN_STATUS_BUCKET : PAYOUT_STATUS_BUCKET;
  return map[status] ?? null;
}

/**
 * Raw backend status → i18n key (`paymentsActivity` namespace). Shared by the pill, the filter menu and the
 * timeline so the wording never diverges between them. The main-flow labels match the old bucket wording
 * (Pendiente / En proceso / Acreditado / Pagado); terminal negatives stay granular (Rechazado / Revertido /
 * Cancelado ≠ Fallido — they mean different things to a finance user). Unknown/new runtime statuses fall back
 * to the raw enum value — callers must treat a missing key as "render the raw status".
 */
export const STATUS_LABEL_KEYS: Partial<Record<string, keyof PaymentsActivityTranslations>> = {
  [PayinRuntimeStatus.CREATED]: "status_value_created",
  [PayinRuntimeStatus.PENDING]: "status_value_pending",
  [PayinRuntimeStatus.CONFIRMED]: "status_value_confirmed",
  [PayinRuntimeStatus.REJECTED]: "status_value_rejected",
  [PayinRuntimeStatus.FAILED]: "status_value_failed",
  [PayinRuntimeStatus.REVERSED]: "status_value_reversed",
  [PayoutStatus.PROCESSING]: "status_value_processing",
  [PayoutStatus.PAID]: "status_value_paid",
  [PayoutStatus.CANCELLED]: "status_value_cancelled",
};

/** Status filter options per direction — single-select (the API takes one enum value, no multi). */
export const STATUS_FILTER_OPTIONS: Record<ActivityDirection, readonly string[]> = {
  payins: Object.values(PayinRuntimeStatus),
  payouts: Object.values(PayoutStatus),
};

/**
 * Corridor filter — sends only `country` (ISO alpha-3): in the runtime the country implies rail + currency
 * (BRA→PIX/BRL, MEX→SPEI/MXN, ARG→CBU-CVU/ARS), and filtering by country alone stays correct if new rails
 * land later. `rail` is display-only; where the wire enum differs from the label (ARG `CBU_CVU`) it derives
 * from `formatRail` so the mapping has a single source of truth.
 */
export interface CorridorOption {
  country: string;
  currency: string;
  rail: string;
}
export const CORRIDOR_OPTIONS: readonly CorridorOption[] = [
  { country: "BRA", currency: "BRL", rail: "PIX" },
  { country: "MEX", currency: "MXN", rail: "SPEI" },
  { country: "ARG", currency: "ARS", rail: formatRail("CBU_CVU") },
  { country: "COL", currency: "COP", rail: formatRail("BREB") },
];

/** Rolling created_at windows for the `from` filter (days). */
export const DATE_RANGE_OPTIONS: readonly number[] = [7, 30, 90];
